import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

import { useIsAuthenticated, useMsal } from "@azure/msal-react";

import { Loading } from "./outside-components";

/**
 * Single, persistent loading overlay for the whole startup — MSAL init AND the
 * authenticated bootstrap.
 */
type BootstrapGateContextValue = { markReady: () => void };

const BootstrapGateContext = createContext<BootstrapGateContextValue | null>(null);

/**
 * Returns `markReady`, to be called by the deepest bootstrap step once the app
 * is ready to be shown (or has reached a terminal error state).
 */
export const useBootstrapGate = (): BootstrapGateContextValue => {
  return useContext(BootstrapGateContext) ?? { markReady: () => {} };
};

export const BootstrapGate: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // Stable identity so `children` (the MSAL templates, passed from a parent that
  // does not re-render when `isReady` flips) are not re-rendered when the overlay
  // is removed.
  const value = useMemo<BootstrapGateContextValue>(
    () => ({ markReady: () => setIsReady(true) }),
    []
  );

  const isOnLoginScreen = !isAuthenticated && (inProgress === "none" || inProgress === "login");
  const showOverlay = !isReady && !isOnLoginScreen;

  return (
    <BootstrapGateContext.Provider value={value}>
      {children}
      {showOverlay && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100000,
            background: "#fff",
          }}
        >
          <Loading />
        </div>
      )}
    </BootstrapGateContext.Provider>
  );
};

export const BootstrapReady = (): null => {
  const { markReady } = useBootstrapGate();
  useEffect(() => {
    markReady();
  }, [markReady]);
  return null;
};
